// @ts-nocheck
// Per-player edit dialog for the Team Detail page (/admin/team/:id).
// Edits ONLY the player's registration data: first name, last name, email, and
// the registration's custom fields (all stored on `player.info`). It deliberately
// does NOT touch the player's linked account / "profile data" (the separate
// UserProfile via player.user) — those stay read-only.
// Submitting sends the FULL merged `info` (the update endpoint $set-replaces it),
// and the backend re-derives `player.name` + emails the waiver if an email was
// added/changed and the registration requires one.
import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useAuth } from '@/auth/clerkShim';
import usePlayerStore from '@/store/playerStore';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Pencil, Loader2 } from 'lucide-react';

const IDENTITY_FIELD_IDS = ['first_name', 'last_name', 'email'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function PlayerEditDialog({ player, registration, onSaved }) {
  const { getToken } = useAuth();
  const updatePlayer = usePlayerStore((s) => s.updatePlayer(getToken));

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const fields = _.get(registration, 'fields', []) || [];
  const customFields = fields.filter((f) => f && !IDENTITY_FIELD_IDS.includes(f.fieldId));

  // Seed the form from the player's current info each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    const info = player?.info || {};
    const init = {
      first_name: info.first_name ?? '',
      last_name: info.last_name ?? '',
      email: info.email ?? player?.email ?? '',
    };
    customFields.forEach((f) => {
      init[f.fieldId] = info[f.fieldId] ?? (f.fieldType === 'multi-select' ? [] : '');
    });
    setForm(init);
  }, [open]);

  const setVal = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const emailValid = !form.email || EMAIL_RE.test(String(form.email).trim());

  const save = async () => {
    if (saving) return;
    if (!emailValid) {
      toast.error('Enter a valid email address.');
      return;
    }
    setSaving(true);
    try {
      // Merge edits into the existing info so custom fields we didn't render are
      // preserved (the update endpoint replaces `info` wholesale).
      const info = { ...(player?.info || {}) };
      Object.keys(form).forEach((k) => {
        const v = form[k];
        info[k] = typeof v === 'string' ? v.trim() : v;
      });
      if (info.email) info.email = String(info.email).trim().toLowerCase();
      const name = `${info.first_name || ''} ${info.last_name || ''}`.trim();

      await updatePlayer({ _id: player._id, name, info });
      setOpen(false);
      onSaved && onSaved();
    } finally {
      setSaving(false);
    }
  };

  const renderCustom = (f) => {
    const v = form[f.fieldId];
    switch (f.fieldType) {
      case 'select':
        return (
          <Select value={v || ''} onValueChange={(val) => setVal(f.fieldId, val)}>
            <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {(f.options || []).map((opt) => (
                <SelectItem key={String(opt)} value={String(opt)}>{String(opt)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'boolean':
        return (
          <div className="flex h-9 items-center">
            <Checkbox checked={!!v} onCheckedChange={(c) => setVal(f.fieldId, !!c)} />
          </div>
        );
      case 'date':
        return <Input type="date" value={v || ''} onChange={(e) => setVal(f.fieldId, e.target.value)} />;
      case 'number':
        // Repo rule: never <input type="number"> — use text + numeric keypad.
        return (
          <Input
            type="text" inputMode="numeric"
            value={v ?? ''}
            onChange={(e) => setVal(f.fieldId, e.target.value.replace(/[^0-9.]/g, ''))}
          />
        );
      case 'multi-select':
        return (
          <div className="flex flex-wrap gap-3">
            {(f.options || []).map((opt) => {
              const arr = Array.isArray(v) ? v : [];
              return (
                <label key={String(opt)} className="flex items-center gap-1.5 text-sm">
                  <Checkbox
                    checked={arr.includes(opt)}
                    onCheckedChange={(c) => {
                      const next = new Set(arr);
                      if (c) next.add(opt); else next.delete(opt);
                      setVal(f.fieldId, Array.from(next));
                    }}
                  />
                  {String(opt)}
                </label>
              );
            })}
          </div>
        );
      default:
        return <Input value={v ?? ''} onChange={(e) => setVal(f.fieldId, e.target.value)} />;
    }
  };

  return (
    <>
      <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit player" onClick={() => setOpen(true)}>
        <Pencil className="h-4 w-4" />
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!saving) setOpen(o); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit player</DialogTitle>
            <DialogDescription>
              Update this player's registration details. Their linked account profile isn't changed here.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>First name</Label>
                <Input value={form.first_name ?? ''} onChange={(e) => setVal('first_name', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Last name</Label>
                <Input value={form.last_name ?? ''} onChange={(e) => setVal('last_name', e.target.value)} />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email ?? ''}
                onChange={(e) => setVal('email', e.target.value)}
                aria-invalid={!emailValid}
              />
              {!emailValid && <p className="text-xs text-destructive">Enter a valid email address.</p>}
              <p className="text-xs text-muted-foreground">
                Adding or changing the email sends the waiver to this address when the registration requires one.
              </p>
            </div>

            {customFields.map((f) => (
              <div key={f.fieldId} className="space-y-1">
                <Label>{f.name}{f.required ? ' *' : ''}</Label>
                {renderCustom(f)}
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving || !emailValid}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
